import {
  CircularProgress,
  IconButton,
  TableCell,
  Tooltip,
  Box,
  Button,
  Stack,
} from "@mui/material";
import {
  ContentPaste,
  Autorenew,
  Description,
  Edit,
  FileDownload,
  Sick,
  InfoOutlined,
} from "@mui/icons-material";
import { isMobile } from "react-device-detect";
import { GetBinary } from "../../../../../services/fhir/patientsummary/Binary/GetBinary";
import { usePractitionerRoleContext } from "../../../../../contexts/PractitionerRoleContext";
import { PostDocumentReference } from "../../../../../services/fhir/patientsummary/documentreference/PostDocumentReference";
import { useSessionContext } from "../../../../../contexts/SessionContext";
import { useState } from "react";
import { useInterventionsContext } from "../../../../../contexts/InterventionsContext";
import { GetAppointment, GetPatient } from "../../../../../services/fhir";
import { useFhirData } from "../../../../../utils/useFhirData";
import { useLocationContext } from "../../../../../contexts/LocationContext";
import { Base64SignPDF } from "../../../../../services/dsig";
import { toast } from "react-toastify";
import { JustificationModal } from "./JustificationModal";
import {
  DocumentVisualization,
  readDocSlot,
  releaseDocIds,
} from "@/pages/medicalcare/components/DocumentVisualization/DocumentVisualization";
import { SignatureDialog } from "@/pages/medicalcare/components/prescription/SignatureDialog";
import { downloadFile } from "@/utils/fileDownloadHelper";
import {
  extractDocumentReferenceAuthorData,
  hasValidProfessionalDocumentRefs,
} from "@/utils/fhirDocumentUtils";
import { usePreferMobileCards } from "../../../context/EhrRecordsLayoutContext";
import {
  EhrMobileActionStack,
  ehrMobileActionBtnSx,
} from "../../mobile/EhrMobileActionStack";
import { mapMedicationRequestToIntervention } from "@/pages/medicalcare/utils/mapMedicationRequestToIntervention";
import { enrichMedicationsFromCmed } from "@/pages/medicalcare/utils/enrichMedicationInterventionFromCmed";

export function PrescriptionsActions({
  row,
  handleOpen,
  setBinaryUrl,
  setRefetch,
  openVisualizer,
  verifyVisualizer,
  cardActions,
  labeledMobileActions,
  onPrepareReissue,
}: any) {
  const { access, user } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();
  const { location } = useLocationContext();

  const [patient, setPatient] = useState<any>();
  const [locationData, setLocationData] = useState<any>();

  const { action, selectedInterventions, setInterventionsList } =
    useInterventionsContext();

  const [originalDocument, setOriginalDocument] = useState<any>();
  const [base64, setBase64] = useState<any>();

  const [signatureDialogOpen, setSignatureDialogOpen] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [item, setItem] = useState<any>([]);

  const [openJustification, setOpenJustification] = useState(false);

  // Extração alinhada ao contrato de AuxiliarData.DocumentReference (oid-cpf / oid-cnpj)
  const authorData = extractDocumentReferenceAuthorData(row?.fullResource);

  const objData = {
    organization: {
      id: authorData.organizationId,
      alias: authorData.organizationAlias,
      name: authorData.organizationName,
    },
    practitioner: {
      id: authorData.practitionerId,
      name: authorData.practitionerName,
    },
    servicePratice: authorData.practiceSetting,
    time: {
      start: row?.fullResource?.period?.start,
      end: row?.fullResource?.period?.end,
    },
    relatesTo: authorData.relatesTo,
    codeValue: row?.fullResource?.type?.coding?.[0]?.code,
    codeDisplay: row?.fullResource?.type?.coding?.[0]?.display,
    categoryValue: row?.fullResource?.category?.[0]?.coding?.[0]?.code,
    categoryDisplay: row?.fullResource?.category?.[0]?.coding?.[0]?.display,
  };

  const basedOn = row?.fullResource?.basedOn
    ?.map(
      (el: any) =>
        `<fhir:basedOn>
    <fhir:reference value="${el?.reference}"/>
    </fhir:basedOn>
    `
    )
    ?.join("");

  const encounter = {
    id: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Encounter"))
      ?.reference?.split("/")?.[1],

    appointmentId: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Appointment"))
      ?.reference?.split("/")?.[1],
    requester: {
      // Só monta reference válida quando o PractitionerRole já carregou
      reference: userPractitionerRole?.id
        ? `PractitionerRole/${userPractitionerRole.id}`
        : undefined,
      display: authorData.practitionerName,
    },
    subject: row?.fullResource?.subject?.reference?.split("-")?.[1],
  };

  // Bloqueia assinatura/reemissão se as refs não garantirem inclusão do profissional no XML
  const canSignOrReissue =
    access?.type === "professional" &&
    !!row?.fullResource &&
    hasValidProfessionalDocumentRefs(objData, encounter) &&
    userPractitionerRole?.id ===
      row?.fullMedicationResource?.[0]?.resource?.requester?.reference?.split(
        "/"
      )?.[1];

  /**
   * FUNÇÃO DE DOWNLOAD ATUALIZADA
   *
   * Funciona tanto no browser quanto no React Native
   * Usa o helper downloadFile que detecta o ambiente automaticamente
   */
  async function downloadBase64File() {
    try {
      console.log("[PrescriptionsActions] Baixando documento assinado...");

      const response = await GetBinary({
        urlBinary: row?.binaryUrl,
      });

      // Usa o helper que funciona em ambos os ambientes (browser + React Native)
      downloadFile(
        response?.data, // Base64 do arquivo
        "Receita.pdf", // Nome do arquivo
        response?.contentType // Tipo MIME
      );

      console.log("[PrescriptionsActions] Download concluído!");
    } catch (err) {
      console.error("[PrescriptionsActions] Erro ao baixar:", err);
      toast.error("Erro ao baixar documento.");
    }
  }

  const handleSignature = async (
    provider: "vidaas" | "safeid",
    password?: string,
    lifeTime?: number
  ) => {
    // Não envia PostDocumentReference com payload incompleto (evita author=Patient)
    if (!canSignOrReissue) {
      toast.warn(
        "Dados do profissional/organização incompletos. Aguarde o carregamento e tente novamente."
      );
      return;
    }

    try {
      setLoading(true);
      toast.warn("Aguardando verificação e segurança do seu provedor!");
      const signResponse = await Base64SignPDF({
        provider: provider,
        ...(provider === "safeid" && {
          userPassword: password,
        }),
        pdfBase64: base64
          ? base64?.[0]?.[`special-0`]?.base64?.split(",")?.[1] ||
            base64?.[0]?.['simple-medication']?.base64?.split(",")?.[1] ||
            base64?.[0]?.['simple-supplement']?.base64?.split(",")?.[1] ||
            base64?.[0]?.['simple-herbalMedicine']?.base64?.split(",")?.[1] ||
            base64?.[0]?.simple?.base64?.split(",")?.[1]
          : originalDocument,
        cpfCnpj: user?.username,
        lifeTime: lifeTime ?? 0,
      });

      if (!signResponse?.length) throw new Error("");
      const prescriptionSlot =
        readDocSlot(base64, "special-0") ||
        readDocSlot(base64, "simple-medication") ||
        readDocSlot(base64, "simple-supplement") ||
        readDocSlot(base64, "simple-herbalMedicine") ||
        readDocSlot(base64, "simple");

      await PostDocumentReference(
        {
          ...objData,
          base64: signResponse,
        },
        encounter,
        basedOn,
        true,
        prescriptionSlot?.uuid,
        prescriptionSlot?.idValue,
      );
      releaseDocIds(patient?.id?.split("-")?.[1], prescriptionSlot?.key);
      toast.success("Assinatura realizada!");
    } catch (error) {
      toast.warn(
        "Não foi possível realizar a assinatura, por favor tente novamente!"
      );

      console.error("HandleSignature error:", error);
    } finally {
      setLoading(false);
      setRefetch((prev: boolean) => !prev);
    }
  };

  async function generateItem() {
    try {
      setLoading(true);
      const patient = await GetPatient({ identifier: encounter?.subject });
      setPatient(patient?.data);

      const appointment = await GetAppointment({
        ehrrunner: encounter?.subject,
        _id: encounter?.appointmentId,
      });

      const locationReference = useFhirData.findParticipant(
        appointment?.entry?.[0]?.resource?.participant,
        "Location"
      );

      const selectedLocation = location?.find((el: any) =>
        el?.resource?.id?.includes(locationReference?.reference.split("/")[1])
      )?.resource;

      setLocationData(selectedLocation);

      // Mesmo pipeline do atendimento (edição): map FHIR → enrich CMED
      // Garante PDF idêntico ao TabsAssinatura / PrescriptionActions
      const resources =
        row?.fullMedicationResource
          ?.map((e: any) => e?.resource)
          ?.filter(Boolean) || [];

      const mapped = resources.map(mapMedicationRequestToIntervention);
      const enriched = await enrichMedicationsFromCmed(mapped);

      const results = enriched.map((el: any) => ({
        ...el,
        refact: true,
        sign: false,
      }));

      if (results?.length > 0 && !item?.length) {
        setItem(results);
        // DocumentVisualization (refact) gera o base64 — não setar itens como base64
        results.forEach((e: any) => action(e));
      }
    } catch (error) {
      console.error("GenerateItem error:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleOpenJustification = () => {
    setOpenJustification(true);
  };

  const handleCloseJustification = () => {
    setOpenJustification(false);
  };

  const isMobileCardLayout = usePreferMobileCards();

  // Preview PDF já gerado pelo DocumentVisualization (shape com *.base64)
  const pdfPreviewBase64 =
    base64?.[0]?.[`special-0`]?.base64?.split(",")?.[1] ||
    base64?.[0]?.["simple-medication"]?.base64?.split(",")?.[1] ||
    base64?.[0]?.["simple-supplement"]?.base64?.split(",")?.[1] ||
    base64?.[0]?.["simple-herbalMedicine"]?.base64?.split(",")?.[1] ||
    base64?.[0]?.simple?.base64?.split(",")?.[1];

  // Após o PDF pronto, desmonta DocumentVisualization para não regenerar visualize em loop
  const shouldRenderDocVisualization =
    !!item?.length && !loading && !!patient && !pdfPreviewBase64;

  const modals = (
    <>
      {signatureDialogOpen && base64?.[0] && item ? (
        <SignatureDialog
          visualize={
            base64 && item
              ? pdfPreviewBase64 || originalDocument
              : originalDocument
          }
          isOpen={signatureDialogOpen}
          handleClose={() => {
            setSignatureDialogOpen(false);
            const indexes = item?.map((e: any) =>
              selectedInterventions?.all.indexOf(e)
            );

            setInterventionsList(
              selectedInterventions?.all?.filter(
                (e: any, index: number) => !indexes?.includes(index)
              )
            );

            setItem([]);
          }}
          handleSignature={handleSignature}
          row={{ sign: "Pendente" }}
          loading={loading}
        />
      ) : null}

      {openJustification ? (
        <JustificationModal
          open={openJustification}
          onClose={handleCloseJustification}
          note={row?.note ? row?.note : "-"}
          status={row?.status}
        />
      ) : null}
    </>
  );

  if (isMobileCardLayout && row?.binaryUrl) {
    return (
      <>
        <EhrMobileActionStack>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<Description />}
            sx={ehrMobileActionBtnSx}
            onClick={() => {
              setBinaryUrl(row?.binaryUrl);
              handleOpen();
            }}
          >
            Visualizar receita
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<FileDownload />}
            sx={ehrMobileActionBtnSx}
            onClick={() => {
              downloadBase64File();
            }}
          >
            Baixar receita
          </Button>

          {verifyVisualizer &&
            verifyVisualizer(row?.encounterId) &&
            access?.type === "professional" && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<ContentPaste />}
                sx={ehrMobileActionBtnSx}
                onClick={() => {
                  openVisualizer(row?.encounterId);
                }}
              >
                Visualizar atendimento
              </Button>
            )}

          {canSignOrReissue && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={
                  loading ? (
                    <CircularProgress size={18} />
                  ) : !row?.sign ? (
                    <Edit />
                  ) : (
                    <Autorenew />
                  )
                }
                sx={ehrMobileActionBtnSx}
                disabled={loading}
                onClick={async () => {
                  if (onPrepareReissue && row?.sign) {
                    onPrepareReissue(row);
                    return;
                  }
                  setLoading(true);
                  const response = await GetBinary({
                    urlBinary: row?.binaryUrl,
                  });
                  setOriginalDocument(response?.data);
                  await generateItem();
                  setSignatureDialogOpen(true);
                }}
              >
                {!row?.sign ? "Assinar documento" : "Reemitir documento"}
              </Button>
            )}

          {(row.status === "stopped" || row.status === "completed") &&
            row.note !== "-" && (
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={
                  row.status === "stopped" ? <Sick /> : <InfoOutlined />
                }
                sx={ehrMobileActionBtnSx}
                onClick={() => handleOpenJustification()}
              >
                {row.status === "stopped"
                  ? "Ver motivo da interrupção"
                  : "Ver observação do paciente"}
              </Button>
            )}
        </EhrMobileActionStack>

        {shouldRenderDocVisualization ? (
          <div style={{ display: "none" }}>
            <DocumentVisualization
              refact
              setBase64={setBase64}
              dataPdf={[
                {
                  patientCPF: patient?.id?.split("-")?.[1],
                  patientData: patient?.name?.[0]?.text || "",
                  professionalData: userPractitionerRole,
                  location: locationData,
                },
              ]}
            />
          </div>
        ) : null}

        {modals}
      </>
    );
  }

  return (
    <>
      {cardActions ? (
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <Button
            fullWidth
            onClick={() => {
              downloadBase64File();
            }}
            startIcon={<FileDownload />}
            variant="contained"
            color="primary"
          >
            Documento assinado
          </Button>
        </Box>
      ) : (
        <>
          <TableCell
            align="center"
            sx={{
              "&.MuiTableCell-root": {
                borderBottom: "none !important",
                padding: "4px 8px",
              },
            }}
          >
            {row?.binaryUrl && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{
                  flexWrap: "nowrap",
                  display: "inline-flex",
                  margin: 0,
                }}
              >
                {!isMobile && (
                  <Tooltip title="Visualizar receita">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setBinaryUrl(row?.binaryUrl);
                        handleOpen();
                      }}
                    >
                      <Description />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Baixar">
                  <IconButton
                    size="small"
                    onClick={() => {
                      downloadBase64File();
                    }}
                  >
                    <FileDownload />
                  </IconButton>
                </Tooltip>

                {verifyVisualizer &&
                  verifyVisualizer(row?.encounterId) &&
                  access?.type === "professional" && (
                    <Tooltip title="Visualizar atendimento">
                      <IconButton
                        size="small"
                        onClick={() => {
                          openVisualizer(row?.encounterId);
                        }}
                      >
                        <ContentPaste />
                      </IconButton>
                    </Tooltip>
                  )}

                {canSignOrReissue && (
                    <Tooltip
                      title={
                        !row?.sign ? "Assinar documento" : "Reemitir documento"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (onPrepareReissue && row?.sign) {
                            onPrepareReissue(row);
                            return;
                          }
                          setLoading(true);
                          const response = await GetBinary({
                            urlBinary: row?.binaryUrl,
                          });
                          setOriginalDocument(response?.data);
                          await generateItem();
                          setSignatureDialogOpen(true);
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={20} />
                        ) : !row?.sign ? (
                          <Edit />
                        ) : (
                          <Autorenew />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
              </Stack>
            )}

            {shouldRenderDocVisualization ? (
              <>
                <div style={{ display: "none" }}>
                  <DocumentVisualization
                    refact
                    setBase64={setBase64}
                    dataPdf={[
                      {
                        patientCPF: patient?.id?.split("-")?.[1],
                        patientData: patient?.name?.[0]?.text || "",

                        professionalData: userPractitionerRole,
                        location: locationData,
                      },
                    ]}
                  />
                </div>
              </>
            ) : (
              <></>
            )}

            <Stack
              direction="row"
              spacing={1}
              pl={1}
              justifyContent="center"
              sx={{
                flexWrap: "nowrap",
                display: "inline-flex",
                margin: 0,
              }}
            >
              {(row.status === "stopped" || row.status === "completed") &&
                row.note !== "-" && (
                  <Tooltip
                    title={
                      row.status === "stopped"
                        ? "Ver motivo da interrupção"
                        : "Ver observação do paciente"
                    }
                  >
                    <IconButton
                      onClick={() => handleOpenJustification()}
                      size="small"
                    >
                      {row.status === "stopped" ? <Sick /> : <InfoOutlined />}
                    </IconButton>
                  </Tooltip>
                )}
            </Stack>
          </TableCell>
        </>
      )}

      {modals}
    </>
  );
}
